import { Injectable } from '@angular/core';
import { ModifiedFormData } from '../types/tvd-types';
import { MetricCostAssumptionsPayloadBuilder } from '../builders/metric-cost-assumptions-payload.builder';
import { CostAssumptionsLayoutResolver } from '../resolvers/cost-assumptions-layout.resolver';
import { TVDService } from '../services/tvd.service';
import { TVDModel } from '../types/tvd-model';

/**
 * Orchestrates the application of cost assumptions.
 *
 * Responsibilities:
 * - Persist user-entered cost assumptions
 * - Resolve the correct payload layout
 * - Build the API payload
 * - Refresh TVD data using the API as the source of truth
 * - Recalculate any client-side derived totals
 *
 * Non-responsibilities:
 * - Payload construction (MetricCostAssumptionsPayloadBuilder)
 * - Layout determination (CostAssumptionsLayoutResolver)
 * - API communication (TVDService)
 * - UI concerns (modals, notifications, loading indicators)
 */
@Injectable({ providedIn: 'root' })
export class CostAssumptionsOrchestrator {

  constructor(
    private readonly _tvdService: TVDService,
    private readonly _layoutResolver: CostAssumptionsLayoutResolver,
    private readonly _payloadBuilder: MetricCostAssumptionsPayloadBuilder
  ) {}

  async applyCostAssumptions(
    formData: ModifiedFormData
  ): Promise<void> {

    this._tvdService.setFormData(formData);

    const layout = this._layoutResolver.resolve();

    const payload = this._payloadBuilder.build(
      formData,
      layout
    );

    await this._tvdService.refreshTVDDataWithAssumptions(
      payload
    );

    const tvdModel = this._tvdService.getCurrentTvdModel();

    this._tvdService.recalculateTvdTotals(
      tvdModel,
      tvdModel?.custTileDict
    );
  }

  /**
   * Builds a preview of the supplied cost assumptions.
   *
   * Preview is strictly non-committing: it must never persist form data,
   * mutate applied TVD state, or refresh the TVD main page. The returned
   * model is a detached copy used solely to render the preview dialog and is
   * discarded when the dialog is closed without saving.
   */
  async previewCostAssumptions(
    formData: ModifiedFormData
  ): Promise<TVDModel | undefined> {

    const layout =
      this._layoutResolver.resolve();

    const payload =
      this._payloadBuilder.build(
        formData,
        layout
      );

    return await this._tvdService
      .buildPreviewTvdModel(
        payload
      );
  }
}